// src/components/barista/OrderCard.jsx
// Separate OrderCard component to prevent scroll issues

import { useEffect, useCallback, useRef, memo } from 'react'
import { Clock, Users, ChefHat, Truck, MapPin, Utensils, Package, Printer } from 'lucide-react'
import BaristaService from '../../services/baristaService'
import { cn } from '../../utils/cn'

// Scrollable Items Component - Completely isolated
const ScrollableItemsList = memo(({ orderItems }) => {
  const scrollContainerRef = useRef(null)
  const scrollPositionRef = useRef(0)
  
  // Preserve scroll position across re-renders
  useEffect(() => {
    if (scrollContainerRef.current && scrollPositionRef.current > 0) {
      scrollContainerRef.current.scrollTop = scrollPositionRef.current
    }
  })
  
  const handleScroll = useCallback((e) => {
    scrollPositionRef.current = e.target.scrollTop
  }, [])
  
  return (
    <div 
      ref={scrollContainerRef}
      className="h-full overflow-y-auto overscroll-contain"
      onScroll={handleScroll}
      style={{
        scrollBehavior: 'auto',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin'
      }}
    >
      <div className="p-3 space-y-2">
        {orderItems && orderItems.length > 0 ? (
          orderItems.map((item, itemIndex) => {
            if (!item || !item.productId) return null
            
            return (
              <div 
                key={`item-${item._id || itemIndex}-${item.productId._id || 'unknown'}`}
                className="flex items-center space-x-3 bg-gray-50 p-2 rounded-md border border-gray-100"
              >
                <div className="w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                  {item.quantity || 0}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900 text-sm truncate">
                    {item.productId.name || 'Unknown Product'}
                  </div>
                </div>
              </div>
            )
          })
        ) : (
          <div className="flex items-center justify-center h-32 text-gray-500">
            <p className="text-sm">No items found</p>
          </div>
        )}
      </div>
    </div>
  )
})

ScrollableItemsList.displayName = 'ScrollableItemsList'

// Order Card Component
const OrderCard = memo(({ 
  order, 
  onStatusChange, 
  onPrint, 
  getOrderTypeIcon, 
  completedOrderTimers, 
  activeTab 
}) => {
  const orderInfo = order.order
  const statusInfo = BaristaService.getStatusDisplayInfo(order.status)
  const buttonConfig = BaristaService.getStatusButtonConfig(order.status)
  const totalItems = BaristaService.calculateTotalItems(order.orderItems)
  const orderTime = BaristaService.formatDateTime(orderInfo.orderDate)
  const orderNumber = orderInfo.orderNumber?.replace('ORD', '') || order._id.slice(-6)
  
  // Calculate remaining time for completed orders
  const completionTime = completedOrderTimers.get(order._id)
  const remainingTime = completionTime ? Math.max(0, 20 - Math.floor((Date.now() - completionTime) / 1000)) : 0
  const showTimer = order.status === 'completed' && remainingTime > 0 && activeTab === 'open'

  const handleButtonClick = useCallback(() => {
    if (buttonConfig) {
      if (buttonConfig.action === 'print') {
        onPrint(order)
      } else if (buttonConfig.action === 'status_change' || buttonConfig.nextStatus) {
        onStatusChange(order._id, buttonConfig.nextStatus)
      }
    }
  }, [buttonConfig, onPrint, onStatusChange, order])

  return (
    <div className={cn(
      "bg-white rounded-lg border-2 shadow-lg hover:shadow-xl transition-all duration-200 flex flex-col h-[400px]",
      order.status === 'pending' && "border-yellow-300 bg-yellow-50/50",
      order.status === 'preparing' && "border-blue-300 bg-blue-50/50", 
      order.status === 'completed' && "border-green-300 bg-green-50/50"
    )}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200 flex-shrink-0">
        {/* First Row - Order Number and Status */}
        <div className="flex items-center justify-between mb-3">
          <span className="text-xl font-bold text-gray-900">#{orderNumber}</span>
          <div className={cn(
            "px-3 py-1 rounded-full text-xs font-bold border-2",
            statusInfo.color
          )}>
            {statusInfo.name}
          </div>
        </div>
        
        {/* Second Row - Date/Time and Order Type */}
        <div className="flex items-center justify-between text-sm text-gray-600">
          <div className="flex items-center space-x-2">
            <Clock className="h-4 w-4" />
            <span className="font-medium">{orderTime}</span>
          </div>
          <div className="flex items-center space-x-2">
            {getOrderTypeIcon(order.orderType)}
            <span className="font-semibold text-gray-700">
              {BaristaService.getOrderTypeDisplayName(order.orderType)}
            </span>
          </div>
        </div>
      </div>

      {/* Order Items - Scrollable Area */}
      <div className="flex-1 min-h-0">
        <ScrollableItemsList 
          orderItems={order.orderItems} 
          orderId={order._id}
        />
      </div>

      {/* Footer */}
      <div className="p-4 border-t border-gray-200 bg-gray-50/80 flex-shrink-0">
        {/* Timer display for completed orders */}
        {showTimer && (
          <div className="mb-2 px-3 py-1 bg-yellow-100 border border-yellow-300 rounded text-center">
            <p className="text-xs text-yellow-800 font-medium">
              Auto-hiding in {remainingTime}s
            </p>
          </div>
        )}
        
        {/* Action Button Row */}
        <div className="flex items-center justify-between">
          <div className="text-sm font-semibold text-gray-700 flex-shrink-0">
            {totalItems} item{totalItems !== 1 ? 's' : ''}
          </div>
          
          {buttonConfig && (
            <button
              onClick={handleButtonClick}
              className={cn(
                "px-6 py-3 rounded-lg font-bold transition-colors text-sm flex items-center space-x-2 shadow-md hover:shadow-lg",
                buttonConfig.className
              )}
            >
              {buttonConfig.action === 'print' && <Printer className="h-4 w-4" />}
              <span>{buttonConfig.text}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  )
})

OrderCard.displayName = 'OrderCard'

export default OrderCard
